/**
 * TanStack Query hooks for `cookieless-fallback`. Thin wrappers over ./api.
 *   - useCookielessFallbackStats   : GET /stats (refetch-on-mount + 10s stale)
 *   - useCookielessFallbackLookup  : POST /lookup
 *   - useCookielessFallbackSweep   : DELETE /expired (invalidates stats)
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	getStats,
	lookup,
	sweepExpired,
	cookielessFallbackQueryKeys,
	type CookielessFallbackStats,
	type CookielessFallbackLookupBody,
	type CookielessFallbackLookupResult,
	type CookielessFallbackSweepResult,
} from "./api";

export function useCookielessFallbackStats(): UseQueryResult<
	CookielessFallbackStats,
	Error
> {
	return useQuery({
		queryKey: cookielessFallbackQueryKeys.stats(),
		queryFn: getStats,
		staleTime: 10_000,
	});
}

export function useCookielessFallbackLookup(): UseMutationResult<
	CookielessFallbackLookupResult,
	Error,
	CookielessFallbackLookupBody
> {
	return useMutation({ mutationFn: lookup });
}

export function useCookielessFallbackSweep(): UseMutationResult<
	CookielessFallbackSweepResult,
	Error,
	void
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => sweepExpired(),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: cookielessFallbackQueryKeys.all });
		},
	});
}
